/**
 * YLI GA4 Cart Interactions Tracking
 * Version: 1.0.0
 * Created: 2026-01-14
 * Purpose: Track cart page interactions (coupon field, shipping calculator, free shipping threshold)
 * GA4 Events: coupon_field_interact, shipping_calculator_use, free_shipping_threshold_view
 * Spec: docs/ga4-events/ga4-event-tracking-plan.md
 */

(function() {
  'use strict';

  // Track which events have been fired to prevent duplicates
  const trackedEvents = new Set();

  // Get cart value from Shopify cart object
  function getCartValue() {
    if (window.theme && window.theme.cart && window.theme.cart.total_price) {
      return (window.theme.cart.total_price / 100).toFixed(2);
    }
    // Fallback: try to find cart total in DOM
    var cartTotal = document.querySelector('[data-cart-total], .cart-total, .cart__total-price');
    if (cartTotal) {
      var totalText = cartTotal.textContent.replace(/[^0-9.]/g, '');
      return parseFloat(totalText) || 0;
    }
    return 0;
  }

  // GA4 Event: coupon_field_interact
  // Track when users interact with coupon/discount code field
  function trackCouponFieldInteract() {
    const couponInputs = document.querySelectorAll('[data-discount-input], input[placeholder*="discount" i], input[placeholder*="coupon" i]');

    couponInputs.forEach(function(input) {
      // Track on focus (user clicks into field)
      input.addEventListener('focus', function() {
        const trackingKey = 'coupon_field_interact';
        if (trackedEvents.has(trackingKey)) return;
        trackedEvents.add(trackingKey);

        const cartValue = getCartValue();

        if (typeof gtag === 'function') {
          gtag('event', 'coupon_field_interact', {
            'cart_value': cartValue,
            'applied_code': input.value ? 'yes' : 'no'
          });
        }
      });
    });
  }

  // GA4 Event: shipping_calculator_use
  // Track when users interact with shipping calculator
  function trackShippingCalculatorUse() {
    // Track when user clicks "Calculate Shipping" button
    const shippingButtons = document.querySelectorAll('.get-rates, [data-shipping-calculator-submit], button[type="button"][class*="shipping"]');

    shippingButtons.forEach(function(button) {
      button.addEventListener('click', function() {
        const trackingKey = 'shipping_calculator_use';
        if (trackedEvents.has(trackingKey)) return;
        trackedEvents.add(trackingKey);

        const cartValue = getCartValue();

        // Check if free shipping threshold is shown
        const freeShipThreshold = document.querySelector('[data-free-shipping-threshold], .free-shipping-message');
        const freeShipThresholdShown = freeShipThreshold ? 'yes' : 'no';

        if (typeof gtag === 'function') {
          gtag('event', 'shipping_calculator_use', {
            'cart_value': cartValue,
            'free_ship_threshold_shown': freeShipThresholdShown
          });
        }
      });
    });

    // Also track when shipping calculator accordion/details is opened
    const shippingDetails = document.querySelector('details[data-collapsible]:has(shipping-calculator)');
    if (shippingDetails) {
      shippingDetails.addEventListener('toggle', function() {
        if (this.open) {
          const trackingKey = 'shipping_calculator_use';
          if (trackedEvents.has(trackingKey)) return;
          trackedEvents.add(trackingKey);

          const cartValue = getCartValue();
          const freeShipThreshold = document.querySelector('[data-free-shipping-threshold], .free-shipping-message');
          const freeShipThresholdShown = freeShipThreshold ? 'yes' : 'no';

          if (typeof gtag === 'function') {
            gtag('event', 'shipping_calculator_use', {
              'cart_value': cartValue,
              'free_ship_threshold_shown': freeShipThresholdShown
            });
          }
        }
      });
    }
  }

  // GA4 Event: free_shipping_threshold_view
  // Track when "Spend $X more for free shipping" message appears
  function trackFreeShippingThreshold() {
    // Use IntersectionObserver to track when threshold message is visible
    const thresholdMessages = document.querySelectorAll('[data-free-shipping-threshold], .free-shipping-message, .cart__message:has-text("free shipping")');

    if (thresholdMessages.length === 0) return;

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          const trackingKey = 'free_shipping_threshold_view';
          if (trackedEvents.has(trackingKey)) return;
          trackedEvents.add(trackingKey);

          const cartValue = getCartValue();

          // Try to extract amount remaining from message text
          const messageText = entry.target.textContent;
          const amountMatch = messageText.match(/\$([0-9.]+)/);
          const amountRemaining = amountMatch ? amountMatch[1] : '0';

          if (typeof gtag === 'function') {
            gtag('event', 'free_shipping_threshold_view', {
              'amount_remaining': amountRemaining,
              'triggered_add_more': 'no' // Will be updated if user adds items after seeing this
            });
          }

          observer.disconnect();
        }
      });
    }, { threshold: 0.5 });

    thresholdMessages.forEach(function(msg) {
      observer.observe(msg);
    });

    // Track if user adds items after seeing threshold
    // Listen for cart updates
    document.addEventListener('cart:item-added', function() {
      if (trackedEvents.has('free_shipping_threshold_view')) {
        // User saw threshold and then added item - update event
        if (typeof gtag === 'function') {
          gtag('event', 'free_shipping_threshold_view', {
            'amount_remaining': '0', // They likely met the threshold
            'triggered_add_more': 'yes'
          });
        }
      }
    });
  }

  // Initialize tracking when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      trackCouponFieldInteract();
      trackShippingCalculatorUse();
      trackFreeShippingThreshold();
    });
  } else {
    trackCouponFieldInteract();
    trackShippingCalculatorUse();
    trackFreeShippingThreshold();
  }

})();
